import type {PinnedItem} from './WorkstreamsSearch';

/**
 * /workstreams: recents ranked by frecency — how often and how lately
 * you opened each — so the ones you live in stay up top without pinning. Each
 * recent keeps its last few visit times; a visit counts more the newer it is
 * (Firefox's address-bar buckets). Pins still sort first.
 */

// Visit times kept per recent; enough to tell "daily" from "once".
export const MAX_VISITS = 10;

const DAY_MS = 24 * 60 * 60 * 1000;

// A visit's weight by age: this week's count most, older ones fade but stay.
const visitWeight = (ageMs: number) =>
  ageMs < 4 * DAY_MS
    ? 100
    : ageMs < 14 * DAY_MS
      ? 70
      : ageMs < 31 * DAY_MS
        ? 50
        : ageMs < 90 * DAY_MS
          ? 30
          : 10;

export const isSameRecent = (a: PinnedItem, b: PinnedItem): boolean =>
  (a.type === 'person' && b.type === 'person' && a.ldap === b.ldap) ||
  (a.type === 'workstream' && b.type === 'workstream' && a.id === b.id) ||
  (a.type === 'view' && b.type === 'view' && a.key === b.key);

/** The sum of its visits' weights; recents saved before visits were kept score 0. */
export const frecencyScore = (item: PinnedItem, now: number): number =>
  (item.visits ?? []).reduce((score, visit) => score + visitWeight(now - visit), 0);

// How long an unpinned recent holds its place right after the pins. A pin you
// rarely open scores low, so without this it would vanish the moment it's
// unpinned (and drop out of storage at the next visit).
export const UNPIN_GRACE_MS = DAY_MS;

const isJustUnpinned = (item: PinnedItem, now: number) =>
  !item.pinned && item.unpinnedAt !== undefined && now - item.unpinnedAt < UNPIN_GRACE_MS;

/** Pinned, or unpinned within the grace: always listed, never dropped. */
const isHeld = (item: PinnedItem, now: number) => Boolean(item.pinned) || isJustUnpinned(item, now);

/** Pin or unpin `item`; an unpin starts its grace. */
export const togglePin = <T extends PinnedItem>(item: T, now: number): T =>
  item.pinned
    ? {...item, pinned: false, unpinnedAt: now}
    : {...item, pinned: true, unpinnedAt: undefined};

const holdRank = (item: PinnedItem, now: number) =>
  item.pinned ? 2 : isJustUnpinned(item, now) ? 1 : 0;

/**
 * Pinned first, then just unpinned, then by score; ties (e.g. old recents with
 * no visits) keep their saved order.
 */
export const byFrecency = <T extends PinnedItem>(items: T[], now: number): T[] =>
  items
    .map((item, index) => ({item, index, score: frecencyScore(item, now)}))
    .sort(
      (a, b) =>
        holdRank(b.item, now) - holdRank(a.item, now) || b.score - a.score || a.index - b.index
    )
    .map(({item}) => item);

// Recents shown at once, beyond which only pins (and just-unpinned) add rows.
export const MAX_VISIBLE_RECENTS = 6;

/**
 * The recents the rail lists (and whose workstreams the strip shows): every pin
 * and just-unpinned recent, then the best-scored others up to `max` in all.
 * Views aren't listed.
 */
export const pickVisibleRecents = <T extends PinnedItem>(
  items: T[],
  now: number,
  max: number = MAX_VISIBLE_RECENTS
): T[] => {
  const ranked = byFrecency(
    items.filter((item) => item.type !== 'view'),
    now
  );
  const heldCount = ranked.filter((item) => isHeld(item, now)).length;
  return ranked.slice(0, Math.max(heldCount, max));
};

/**
 * Recents with their workstreams' current names (a visit can save a placeholder
 * or a name since renamed); `items` itself when none changed.
 */
export const withWorkstreamNames = (
  items: PinnedItem[],
  names: Map<number, string>
): PinnedItem[] => {
  const stale = (item: PinnedItem) =>
    item.type === 'workstream' && names.has(item.id) && names.get(item.id) !== item.name;
  return items.some(stale)
    ? items.map((item) =>
        item.type === 'workstream' && stale(item) ? {...item, name: names.get(item.id)!} : item
      )
    : items;
};

/**
 * Count a visit to `item`: added if new (newest first), else its visit times
 * grow (its saved name and pin stay). Unpinned recents past `maxUnpinned` drop,
 * lowest score first; pinned and just-unpinned ones and the one just visited
 * are always kept (a first visit would otherwise lose to a full list of
 * well-used recents).
 */
export const recordVisit = (
  prev: PinnedItem[],
  item: PinnedItem,
  now: number,
  maxUnpinned: number
): PinnedItem[] => {
  const existing = prev.find((p) => isSameRecent(p, item));
  const visits = [...(existing?.visits ?? []), now].slice(-MAX_VISITS);
  const visited = {...(existing ?? item), visits};
  const next = existing ? prev.map((p) => (p === existing ? visited : p)) : [visited, ...prev];
  const others = byFrecency(
    next.filter((p) => !isHeld(p, now) && p !== visited),
    now
  );
  const kept = new Set(others.slice(0, isHeld(visited, now) ? maxUnpinned : maxUnpinned - 1));
  return next.filter((p) => p === visited || isHeld(p, now) || kept.has(p));
};
